import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Modal,
  FlatList,
  I18nManager,
  Platform,
  Switch,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';
import {
  DeviceType,
  DeviceTypeLabels,
  getAllDeviceTypes,
  getDeviceTypeLabel,
  getDeviceTypeIcon,
} from '../constants/deviceTypes';
import { siteApi, zoneApi, deviceApi } from '../services/api';
import { ScannedDeviceData } from './QRCodeScannerScreen';
import { alarmRulesApi, AlarmRuleTemplate } from '../services/alarmRulesApi';
import { handleError, getErrorMessage, parseError } from '../utils/errorHandler';
import { canManageDevices } from '../utils/permissions';
import { useHeaderHeight } from '@react-navigation/elements';
import {
  calculateCylindricalVolume,
  litersToGallons,
  litersToM3,
  formatVolume,
  type TankShape,
} from '../utils/tankCalculations';

interface Zone {
  id: string;
  name: string;
  siteId: string;
}

interface Site {
  id: string;
  name: string;
}

interface AddDeviceScreenProps {
  navigation: any;
}

export default function AddDeviceScreen({ navigation }: AddDeviceScreenProps) {
  // Header is transparent (see stack navigator) — push content below it
  const headerHeight = useHeaderHeight();
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // حماية فورية من الضغط المتعدد باستخدام useRef
  const isSavingRef = useRef(false);

  // Check permissions
  const hasManagePermission = user?.roles ? canManageDevices(user.roles) : false;

  // Form state
  const [name, setName] = useState('');
  const [deviceType, setDeviceType] = useState<DeviceType>(DeviceType.MULTI_SENSOR);
  const [hasBattery, setHasBattery] = useState(false);
  const [tankCapacityLiters, setTankCapacityLiters] = useState('');
  const [tankHeightCm, setTankHeightCm] = useState('');
  const [tankDiameterCm, setTankDiameterCm] = useState('');
  const [tankUnit, setTankUnit] = useState<'L' | 'm3'>('L');
  const [tankAlertsEnabled, setTankAlertsEnabled] = useState(true);
  const [tankAlertLowCm, setTankAlertLowCm] = useState('');
  const [tankAlertHighCm, setTankAlertHighCm] = useState('');
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [alarmTemplates, setAlarmTemplates] = useState<AlarmRuleTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<AlarmRuleTemplate | null>(null);

  // Modal states
  const [showDeviceTypePicker, setShowDeviceTypePicker] = useState(false);
  const [showSitePicker, setShowSitePicker] = useState(false);
  const [showZonePicker, setShowZonePicker] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // New: Auto-calculate volume state
  const [autoCalculateVolume, setAutoCalculateVolume] = useState(true);
  const [tankShape, setTankShape] = useState<TankShape>('cylindrical');

  const isArabic = i18n.language === 'ar';
  const isRTL = I18nManager.isRTL || isArabic;
  const isTankDevice =
    deviceType === DeviceType.LEVEL ||
    deviceType === DeviceType.WATER ||
    deviceType === DeviceType.DIESEL ||
    name.toLowerCase().includes('tank') ||
    name.toLowerCase().includes('diesel') ||
    name.toLowerCase().includes('water');

  useEffect(() => {
    navigation.setOptions({
      title: isArabic ? 'إضافة جهاز جديد' : 'Add New Device',
      headerTransparent: true, // Maintain transparency for Galaxy background
      headerTintColor: '#ffffff', // Force white text for Galaxy header
      headerStyle: {
        backgroundColor: 'transparent',
        elevation: 0,
        shadowOpacity: 0,
      },
    });

    // Check permissions before loading
    if (!hasManagePermission) {
      Alert.alert(
        isArabic ? 'صلاحية مطلوبة' : 'Permission Required',
        isArabic
          ? 'لا تملك صلاحية إضافة أجهزة. يتطلب ذلك صلاحية مدير أو مشرف.'
          : 'You do not have permission to add devices. This requires Manager or Admin role.',
        [{ text: isArabic ? 'حسناً' : 'OK', onPress: () => navigation.goBack() }],
      );
      return;
    }

    loadSitesAndZones();
    loadAlarmTemplates();
  }, []);

  // Auto-calculate volume when dimensions change
  useEffect(() => {
    if (autoCalculateVolume && tankHeightCm && tankDiameterCm && tankShape === 'cylindrical') {
      try {
        const height = parseFloat(tankHeightCm);
        const diameter = parseFloat(tankDiameterCm);

        if (height > 0 && diameter > 0) {
          const calculatedVolume = calculateCylindricalVolume(height, diameter);
          setTankCapacityLiters(calculatedVolume.toFixed(2));
          logger.info('Auto-calculated tank volume:', calculatedVolume);
        }
      } catch (error) {
        logger.warn('Failed to auto-calculate volume:', error);
      }
    }
  }, [tankHeightCm, tankDiameterCm, autoCalculateVolume, tankShape]);

  const loadAlarmTemplates = async () => {
    try {
      const templates = await alarmRulesApi.getTemplates();
      logger.info('Alarm templates loaded:', templates);
      setAlarmTemplates(templates);
    } catch (error) {
      logger.error('Failed to load alarm templates:', error);
    }
  };

  const loadSitesAndZones = async () => {
    try {
      setLoading(true);
      if (!user?.orgId) {
        logger.warn('No orgId available, cannot load sites and zones');
        return;
      }

      // Load sites and zones using API services
      const [sitesData, zonesData] = await Promise.all([
        siteApi.getSites(user.orgId).catch((err) => {
          logger.error('Failed to load sites:', err);
          return [];
        }),
        zoneApi.getZones(user.orgId).catch((err) => {
          logger.error('Failed to load zones:', err);
          return [];
        }),
      ]);

      logger.info('Sites loaded:', sitesData);
      logger.info('Zones loaded:', zonesData);

      setSites(sitesData);
      if (sitesData.length > 0) {
        setSelectedSite(sitesData[0]);
      }

      setZones(zonesData);
      if (zonesData.length > 0) {
        setSelectedZone(zonesData[0]);
      }
    } catch (error) {
      logger.error('Failed to load sites and zones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // حماية قوية من الضغط المتعدد باستخدام useRef (فوري)
    if (isSavingRef.current) {
      logger.log('⚠️ Already saving (ref check), ignoring duplicate click');
      return;
    }

    // حماية إضافية من state
    if (saving) {
      logger.log('⚠️ Already saving (state check), ignoring duplicate click');
      return;
    }

    if (!name.trim()) {
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'الرجاء إدخال اسم الجهاز' : 'Please enter device name',
      );
      return;
    }

    if (!selectedSite || !selectedZone) {
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'الرجاء اختيار الموقع والمنطقة' : 'Please select site and zone',
      );
      return;
    }

    try {
      // تعيين الحماية فوراً
      isSavingRef.current = true;
      setSaving(true);
      logger.log('🚀 Starting device creation...');
      if (!user?.orgId) {
        throw new Error('Organization ID not found');
      }

      const tankData: any = {};
      if (isTankDevice) {
        if (tankCapacityLiters.trim()) {
          tankData.tankCapacityLiters = parseFloat(tankCapacityLiters);
        }
        if (tankHeightCm.trim()) {
          tankData.tankHeightCm = parseFloat(tankHeightCm);
        }
        if (tankDiameterCm.trim()) {
          tankData.tankDiameterCm = parseFloat(tankDiameterCm);
        }
        if (tankUnit) {
          tankData.tankUnit = tankUnit;
        }
      }

      const deviceData = {
        name: name.trim(),
        orgId: user.orgId,
        siteId: selectedSite.id,
        zoneId: selectedZone.id,
        deviceType,
        hasBattery,
        ...tankData,
        // Remove manual MQTT credentials - let backend generate them
      };

      logger.info('Creating device with data:', deviceData);
      logger.info('User context:', {
        userId: user.id,
        orgId: user.orgId,
        roles: user.roles,
        email: user.email,
      });

      const result = await deviceApi.createDevice(deviceData);

      logger.info('Device created successfully:', result);

      // Apply alarm template if selected
      if (selectedTemplate && result.id) {
        try {
          const templateResult = await alarmRulesApi.applyTemplate(result.id, selectedTemplate.id);
          logger.info('Alarm template applied:', templateResult);
        } catch (templateError) {
          logger.error('Failed to apply alarm template:', templateError);
          // Don't fail the device creation if template fails
        }
      }

      // Create tank level alert rules (optional)
      if (isTankDevice && tankAlertsEnabled && result.id) {
        const low = tankAlertLowCm.trim() ? parseFloat(tankAlertLowCm) : undefined;
        const high = tankAlertHighCm.trim() ? parseFloat(tankAlertHighCm) : undefined;
        if (low !== undefined || high !== undefined) {
          try {
            await alarmRulesApi.createRule({
              deviceId: result.id,
              type: 'level',
              severity: 'warning',
              thresholdLow: low,
              thresholdHigh: high,
            });
          } catch (ruleError) {
            logger.error('Failed to create tank level rule:', ruleError);
          }
        }
      }

      // Prepare MQTT credentials for display
      const topic = `iotedge/v1/${result.orgId}/${result.siteId}/${result.zoneId}/${result.id}/telemetry`;
      const credentials = `MQTT Client ID: ${result.mqttClientId}\nMQTT Username: ${result.mqttUsername}\nMQTT Password: ${result.mqttPassword}\nDevice ID: ${result.id}\nOrg ID: ${result.orgId}\nSite ID: ${result.siteId}\nZone ID: ${result.zoneId}\nTopic: ${topic}`;

      logger.log('✅ Device created successfully:', result);
      logger.log('📋 MQTT Credentials:', {
        username: result.mqttUsername,
        password: result.mqttPassword,
        clientId: result.mqttClientId,
      });

      // على Web: استخدم window alert + confirm بدلاً من Alert
      if (Platform.OS === 'web') {
        const successMessage = (
          isArabic
            ? [
                '✅ تم إضافة الجهاز بنجاح!',
                '',
                '📱 معلومات الجهاز:',
                '━━━━━━━━━━━━━━━━━━━━━━━',
                `📌 الاسم: ${result.name}`,
                `🆔 Device ID: ${result.id}`,
                `🏢 Organization ID: ${result.orgId}`,
                `🏛️ Site ID: ${result.siteId}`,
                `📍 Zone ID: ${result.zoneId}`,
                '',
                '📡 بيانات MQTT (مهمة جداً!):',
                '━━━━━━━━━━━━━━━━━━━━━━━',
                `🔌 Client ID: ${result.mqttClientId}`,
                `👤 اسم المستخدم: ${result.mqttUsername}`,
                `🔑 كلمة المرور: ${result.mqttPassword}`,
                '',
                '📨 Topic للإرسال:',
                topic,
                '',
                '━━━━━━━━━━━━━━━━━━━━━━━',
                '⚠️ احفظ هذه البيانات! لن تظهر مرة أخرى',
                '',
                'موافق = نسخ كل البيانات 📋',
                'إلغاء = تجاهل',
              ]
            : [
                '✅ Device Added Successfully!',
                '',
                '📱 Device Information:',
                '━━━━━━━━━━━━━━━━━━━━━━━',
                `📌 Name: ${result.name}`,
                `🆔 Device ID: ${result.id}`,
                `🏢 Organization ID: ${result.orgId}`,
                `🏛️ Site ID: ${result.siteId}`,
                `📍 Zone ID: ${result.zoneId}`,
                '',
                '📡 MQTT Credentials (Important!):',
                '━━━━━━━━━━━━━━━━━━━━━━━',
                `🔌 Client ID: ${result.mqttClientId}`,
                `👤 Username: ${result.mqttUsername}`,
                `🔑 Password: ${result.mqttPassword}`,
                '',
                '📨 Publish Topic:',
                topic,
                '',
                '━━━━━━━━━━━━━━━━━━━━━━━',
                "⚠️ Save these credentials! Won't show again",
                '',
                'OK = Copy all data 📋',
                'Cancel = Skip',
              ]
        ).join('\n');

        const copyCredentials = window.confirm(successMessage);

        if (copyCredentials) {
          // استخدام navigator.clipboard للويب
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard
              .writeText(credentials)
              .then(() => {
                logger.log('📋 Credentials copied to clipboard (navigator API)');
                alert(
                  isArabic
                    ? '✅ تم نسخ جميع البيانات إلى الحافظة!'
                    : '✅ All credentials copied to clipboard!',
                );
              })
              .catch((err) => {
                logger.error('Failed to copy:', err);
                alert(isArabic ? '❌ فشل النسخ. جرب يدوياً.' : '❌ Copy failed. Try manually.');
              });
          } else {
            // Fallback للمتصفحات القديمة
            try {
              const textArea = document.createElement('textarea');
              textArea.value = credentials;
              textArea.style.position = 'fixed';
              textArea.style.opacity = '0';
              document.body.appendChild(textArea);
              textArea.select();
              document.execCommand('copy');
              document.body.removeChild(textArea);
              logger.log('📋 Credentials copied (fallback method)');
              alert(isArabic ? '✅ تم نسخ البيانات!' : '✅ Credentials copied!');
            } catch (err) {
              logger.error('Fallback copy failed:', err);
              alert(isArabic ? '❌ فشل النسخ' : '❌ Copy failed');
            }
          }
        }

        // إعادة تعيين الحماية
        isSavingRef.current = false;
        setSaving(false);
        logger.log('🔓 Button unlocked (web)');
        navigation.goBack();
        return;
      }

      // على Mobile: استخدم Alert.alert
      // عرض رسالة النجاح
      Alert.alert(
        isArabic ? '✅ تم إضافة الجهاز بنجاح' : '✅ Device Added Successfully',
        isArabic
          ? `📱 معلومات الجهاز:
━━━━━━━━━━━━━━━━━━━
📌 الاسم: ${result.name}
🆔 Device ID: ${result.id}

📡 بيانات MQTT:
━━━━━━━━━━━━━━━━━━━
🔌 Client ID: ${result.mqttClientId}
👤 اسم المستخدم: ${result.mqttUsername}
🔑 كلمة المرور: ${result.mqttPassword}

⚠️ احفظ هذه البيانات!`
          : `📱 Device Information:
━━━━━━━━━━━━━━━━━━━
📌 Name: ${result.name}
🆔 Device ID: ${result.id}

📡 MQTT Credentials:
━━━━━━━━━━━━━━━━━━━
🔌 Client ID: ${result.mqttClientId}
👤 Username: ${result.mqttUsername}
🔑 Password: ${result.mqttPassword}

⚠️ Save these credentials!`,
        [
          {
            text: isArabic ? '📋 نسخ البيانات' : '📋 Copy Credentials',
            onPress: async () => {
              try {
                // دعم كامل لـ Android و iOS و Web
                await Clipboard.setStringAsync(credentials);
                logger.log('📋 Credentials copied to clipboard (mobile)');
              } catch (error) {
                logger.error('Failed to copy on mobile:', error);
              }
              // إعادة تعيين الحماية قبل الرجوع
              isSavingRef.current = false;
              setSaving(false);
              logger.log('🔓 Button unlocked after copying');
              // Navigate back after a short delay
              setTimeout(() => {
                navigation.goBack();
              }, 500);
            },
          },
          {
            text: isArabic ? 'موافق' : 'OK',
            onPress: () => {
              logger.log('🔙 Navigating back to devices list');
              // إعادة تعيين الحماية قبل الرجوع
              isSavingRef.current = false;
              setSaving(false);
              logger.log('🔓 Button unlocked after OK');
              navigation.goBack();
            },
          },
        ],
        { cancelable: false },
      );
    } catch (error: any) {
      logger.error('❌ Device creation failed:', error);
      logger.error('Failed to save device - Full error:', {
        error,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
        stack: error?.stack,
      });

      // More detailed error message
      let errorMessage = 'Unknown error';

      if (error?.response) {
        const status = error.response.status;
        const data = error.response.data;

        // Extract error message from different possible formats
        if (data?.message) {
          errorMessage = data.message;
        } else if (data?.error) {
          errorMessage = data.error;
        } else if (typeof data === 'string') {
          errorMessage = data;
        } else {
          switch (status) {
            case 400:
              errorMessage = isArabic ? 'بيانات غير صحيحة' : 'Invalid data provided';
              break;
            case 401:
              errorMessage = isArabic ? 'غير مصرح بالوصول' : 'Unauthorized access';
              break;
            case 403:
              errorMessage = isArabic
                ? 'لا تملك صلاحية لإضافة أجهزة'
                : 'No permission to add devices';
              break;
            case 404:
              errorMessage = isArabic ? 'الموقع أو المنطقة غير موجود' : 'Site or zone not found';
              break;
            case 500:
              errorMessage = isArabic ? 'خطأ في الخادم' : 'Server error';
              break;
            default:
              errorMessage = `HTTP ${status}: ${data?.message || 'Unknown error'}`;
          }
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // على Web: استخدم window.alert
      if (Platform.OS === 'web') {
        const errorMsg = isArabic
          ? `❌ خطأ في إضافة الجهاز\n\n${errorMessage}\n\nاضغط موافق للمتابعة`
          : `❌ Device Creation Error\n\n${errorMessage}\n\nPress OK to continue`;

        alert(errorMsg);

        // إعادة تعيين الحماية
        isSavingRef.current = false;
        setSaving(false);
        logger.log('🔓 Button unlocked after error (web)');

        // If it's an auth error, go back to login
        if (error?.response?.status === 401) {
          navigation.navigate('Login');
        }
        return;
      }

      // على Mobile: استخدم Alert.alert
      Alert.alert(
        isArabic ? 'خطأ في إضافة الجهاز' : 'Device Creation Error',
        isArabic ? `فشل حفظ الجهاز:\n${errorMessage}` : `Failed to save device:\n${errorMessage}`,
        [
          {
            text: isArabic ? 'حسناً' : 'OK',
            onPress: () => {
              // إعادة تعيين الحماية عند الخطأ
              isSavingRef.current = false;
              setSaving(false);
              logger.log('🔓 Button unlocked after error');
              // If it's an auth error, go back to login
              if (error?.response?.status === 401) {
                navigation.navigate('Login');
              }
            },
          },
          {
            text: isArabic ? 'إعادة المحاولة' : 'Retry',
            onPress: () => {
              // إعادة تعيين الحماية قبل إعادة المحاولة
              isSavingRef.current = false;
              setSaving(false);
              logger.log('🔓 Button unlocked for retry');
              handleSave();
            },
          },
        ],
      );
    } finally {
      // لا نعيد تعيين isSavingRef هنا - يتم في Alert callbacks فقط
      // هذا يمنع الضغط المتعدد قبل ظهور Alert
      logger.log('ℹ️ Device operation completed, waiting for user action');
    }
  };

  const filteredZones = zones.filter((z) => !selectedSite || z.siteId === selectedSite.id);

  if (loading) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: theme.colors.background, paddingTop: headerHeight },
      ]}
    >
      <ScrollView contentContainerStyle={{ paddingTop: Platform.OS === 'ios' ? 110 : 90 }}>
        <View style={styles.content}>
          {/* Organization Info Badge */}
          {user?.organization?.name && (
            <View
              style={[
                styles.orgInfoCard,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
            >
              <Ionicons name="business" size={20} color={theme.colors.primary} />
              <View style={styles.orgInfoText}>
                <Text style={[styles.orgInfoLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'المنظمة' : 'Organization'}
                </Text>
                <Text style={[styles.orgInfoValue, { color: theme.colors.text }]}>
                  {user.organization.name}
                </Text>
              </View>
            </View>
          )}

          {/* User Role Info Badge */}
          {user?.roles && user.roles.length > 0 && (
            <View
              style={[
                styles.roleInfoCard,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
            >
              <Ionicons name="shield-checkmark" size={20} color={theme.colors.primary} />
              <View style={styles.roleInfoText}>
                <Text style={[styles.roleInfoLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'الصلاحية' : 'Role'}
                </Text>
                <Text style={[styles.roleInfoValue, { color: theme.colors.text }]}>
                  {user.roles
                    .map((role) =>
                      typeof role === 'string' ? role : isArabic ? role.nameAr : role.name,
                    )
                    .join(', ')}
                </Text>
              </View>
            </View>
          )}

          {/* User Info Badge */}
          {user?.email && (
            <View
              style={[
                styles.userInfoCard,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
            >
              <Ionicons name="person" size={20} color={theme.colors.primary} />
              <View style={styles.userInfoText}>
                <Text style={[styles.userInfoLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'المستخدم' : 'User'}
                </Text>
                <Text style={[styles.userInfoValue, { color: theme.colors.text }]}>
                  {user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user.email}
                </Text>
              </View>
            </View>
          )}

          {/* ✅ P1-06: QR Code Scanner Button */}
          <TouchableOpacity
            style={[styles.qrButton, { backgroundColor: theme.colors.primary }]}
            onPress={() => {
              navigation.navigate('QRCodeScanner', {
                onScan: (data: ScannedDeviceData) => {
                  // Fill form with scanned data
                  if (data.name) setName(data.name);
                  if (data.deviceType) setDeviceType(data.deviceType as DeviceType);
                  if (data.hasBattery !== undefined) setHasBattery(data.hasBattery);
                  Alert.alert(
                    isArabic ? 'تم المسح بنجاح' : 'Scanned Successfully',
                    isArabic ? 'تم ملء البيانات من رمز QR' : 'Form filled from QR code',
                  );
                },
              });
            }}
            accessible={true}
            accessibilityLabel={isArabic ? 'مسح رمز QR' : 'Scan QR code'}
            accessibilityHint={
              isArabic
                ? 'انقر نقرًا مزدوجًا لفتح ماسح رمز QR وملء بيانات الجهاز تلقائيًا'
                : 'Double tap to open QR code scanner and auto-fill device information'
            }
            accessibilityRole="button"
          >
            <Ionicons name="qr-code-outline" size={24} color="#FFF" />
            <Text style={styles.qrButtonText}>{isArabic ? 'مسح رمز QR' : 'Scan QR Code'}</Text>
          </TouchableOpacity>

          {/* ✅ P1-06: Device Name */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {isArabic ? 'اسم الجهاز' : 'Device Name'} *
            </Text>
            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.card,
                  color: theme.colors.text,
                  borderColor: theme.colors.border,
                  textAlign: isRTL ? 'right' : 'left',
                },
              ]}
              value={name}
              onChangeText={setName}
              placeholder={isArabic ? 'أدخل اسم الجهاز' : 'Enter device name'}
              placeholderTextColor={theme.colors.textSecondary}
              accessible={true}
              accessibilityLabel={isArabic ? 'اسم الجهاز (مطلوب)' : 'Device name (required)'}
              accessibilityHint={
                isArabic ? 'أدخل اسمًا فريدًا للجهاز' : 'Enter a unique name for the device'
              }
            />
          </View>

          {/* ✅ P1-06: Device Type */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {isArabic ? 'نوع الجهاز' : 'Device Type'} *
            </Text>
            <TouchableOpacity
              style={[
                styles.input,
                styles.pickerButton,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
              onPress={() => setShowDeviceTypePicker(true)}
              accessible={true}
              accessibilityLabel={`${
                isArabic ? 'نوع الجهاز (مطلوب)' : 'Device type (required)'
              }: ${getDeviceTypeLabel(deviceType, isArabic ? 'ar' : 'en')}`}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لاختيار نوع الجهاز من القائمة'
                  : 'Double tap to select device type from the list'
              }
              accessibilityRole="button"
            >
              <View style={styles.pickerContent}>
                <Ionicons
                  name={getDeviceTypeIcon(deviceType) as any}
                  size={20}
                  color={theme.colors.primary}
                />
                <Text style={[styles.pickerText, { color: theme.colors.text }]}>
                  {getDeviceTypeLabel(deviceType, isArabic ? 'ar' : 'en')}
                </Text>
              </View>
              <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          </View>

          {isTankDevice && (
            <View
              style={[
                styles.tankConfigCard,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
            >
              <View style={styles.tankConfigHeader}>
                <Ionicons name="water" size={20} color={theme.colors.info} />
                <Text style={[styles.tankConfigTitle, { color: theme.colors.text }]}>
                  {isArabic ? 'إعدادات الخزان' : 'Tank Settings'}
                </Text>
              </View>

              {/* ✅ P1-06: Auto-Calculate Toggle */}
              <View
                style={[
                  styles.switchContainer,
                  {
                    backgroundColor: theme.colors.background,
                    borderColor: theme.colors.border,
                    marginBottom: 12,
                  },
                ]}
              >
                <View style={styles.switchLabel}>
                  <Ionicons name="calculator" size={20} color={theme.colors.success} />
                  <View style={styles.switchTextContainer}>
                    <Text style={[styles.label, { marginBottom: 0, color: theme.colors.text }]}>
                      {isArabic ? 'حساب الحجم تلقائياً' : 'Auto-Calculate Volume'}
                    </Text>
                    <Text
                      style={[styles.hint, { marginTop: 2, color: theme.colors.textSecondary }]}
                    >
                      {isArabic
                        ? 'يحسب السعة من الأبعاد تلقائياً'
                        : 'Calculate capacity from dimensions automatically'}
                    </Text>
                  </View>
                </View>
                <Switch
                  value={autoCalculateVolume}
                  onValueChange={setAutoCalculateVolume}
                  trackColor={{ false: theme.colors.border, true: theme.colors.success + '80' }}
                  thumbColor={
                    autoCalculateVolume ? theme.colors.success : theme.colors.textSecondary
                  }
                  accessible={true}
                  accessibilityLabel={isArabic ? 'حساب الحجم تلقائياً' : 'Auto-calculate volume'}
                  accessibilityHint={
                    autoCalculateVolume
                      ? isArabic
                        ? 'مفعّل. انقر نقرًا مزدوجًا للتبديل إلى الإدخال اليدوي'
                        : 'On. Tap to switch to manual input'
                      : isArabic
                      ? 'غير مفعّل. انقر نقرًا مزدوجًا للحساب التلقائي من الأبعاد'
                      : 'Off. Tap to auto-calculate from dimensions'
                  }
                  accessibilityRole="switch"
                  accessibilityState={{ checked: autoCalculateVolume }}
                />
              </View>

              {/* Tank Shape Selector */}
              {autoCalculateVolume && (
                <View style={styles.fieldContainerSmall}>
                  <Text style={[styles.label, { color: theme.colors.text }]}>
                    {isArabic ? 'شكل الخزان' : 'Tank Shape'}
                  </Text>
                  <View style={styles.unitToggleRow}>
                    <TouchableOpacity
                      style={[
                        styles.unitToggle,
                        { borderColor: theme.colors.border, flex: 1 },
                        tankShape === 'cylindrical' && { backgroundColor: theme.colors.primary },
                      ]}
                      onPress={() => setTankShape('cylindrical')}
                    >
                      <Ionicons
                        name="git-commit-outline"
                        size={16}
                        color={tankShape === 'cylindrical' ? '#fff' : theme.colors.text}
                      />
                      <Text
                        style={[
                          styles.unitToggleText,
                          {
                            color: tankShape === 'cylindrical' ? '#fff' : theme.colors.text,
                            marginLeft: 4,
                          },
                        ]}
                      >
                        {isArabic ? 'أسطواني' : 'Cylindrical'}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.unitToggle,
                        { borderColor: theme.colors.border, flex: 1, opacity: 0.5 },
                      ]}
                      disabled={true}
                    >
                      <Ionicons name="square-outline" size={16} color={theme.colors.text} />
                      <Text
                        style={[styles.unitToggleText, { color: theme.colors.text, marginLeft: 4 }]}
                      >
                        {isArabic ? 'مستطيل' : 'Rectangular'}
                      </Text>
                      <Text style={[styles.comingSoonBadge, { color: theme.colors.textSecondary }]}>
                        ({isArabic ? 'قريباً' : 'Soon'})
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}

              {/* Dimensions */}
              <View style={styles.row}>
                <View style={[styles.fieldContainerSmall, styles.rowItem]}>
                  <Text style={[styles.label, { color: theme.colors.text }]}>
                    {isArabic ? 'ارتفاع الخزان (سم)' : 'Tank Height (cm)'}
                  </Text>
                  <TextInput
                    style={[
                      styles.input,
                      {
                        backgroundColor: theme.colors.background,
                        color: theme.colors.text,
                        borderColor: theme.colors.border,
                        textAlign: isRTL ? 'right' : 'left',
                      },
                    ]}
                    value={tankHeightCm}
                    onChangeText={setTankHeightCm}
                    placeholder={isArabic ? 'مثال: 150' : 'e.g., 150'}
                    placeholderTextColor={theme.colors.textSecondary}
                    keyboardType="numeric"
                  />
                </View>

                <View style={[styles.fieldContainerSmall, styles.rowItem]}>
                  <Text style={[styles.label, { color: theme.colors.text }]}>
                    {isArabic ? 'قطر الخزان (سم)' : 'Tank Diameter (cm)'}
                  </Text>
                  <TextInput
                    style={[
                      styles.input,
                      {
                        backgroundColor: theme.colors.background,
                        color: theme.colors.text,
                        borderColor: theme.colors.border,
                        textAlign: isRTL ? 'right' : 'left',
                      },
                    ]}
                    value={tankDiameterCm}
                    onChangeText={setTankDiameterCm}
                    placeholder={isArabic ? 'مثال: 120' : 'e.g., 120'}
                    placeholderTextColor={theme.colors.textSecondary}
                    keyboardType="numeric"
                  />
                </View>
              </View>

              {/* Calculated Volume Display */}
              {autoCalculateVolume &&
                tankHeightCm &&
                tankDiameterCm &&
                parseFloat(tankHeightCm) > 0 &&
                parseFloat(tankDiameterCm) > 0 && (
                  <View
                    style={[
                      styles.calculatedValueCard,
                      {
                        backgroundColor: theme.colors.success + '10',
                        borderColor: theme.colors.success + '30',
                      },
                    ]}
                  >
                    <View style={styles.calculatedValueHeader}>
                      <Ionicons name="checkmark-circle" size={20} color={theme.colors.success} />
                      <Text style={[styles.calculatedLabel, { color: theme.colors.success }]}>
                        {isArabic ? 'الحجم المحسوب:' : 'Calculated Volume:'}
                      </Text>
                    </View>
                    <View style={styles.calculatedValueRow}>
                      <Text style={[styles.calculatedValue, { color: theme.colors.text }]}>
                        {parseFloat(tankCapacityLiters || '0').toFixed(2)} {isArabic ? 'لتر' : 'L'}
                      </Text>
                      <Text
                        style={[
                          styles.calculatedValueSecondary,
                          { color: theme.colors.textSecondary },
                        ]}
                      >
                        ({litersToGallons(parseFloat(tankCapacityLiters || '0')).toFixed(2)}{' '}
                        {isArabic ? 'جالون' : 'gal'} |{' '}
                        {litersToM3(parseFloat(tankCapacityLiters || '0')).toFixed(3)} m³)
                      </Text>
                    </View>
                  </View>
                )}

              {/* Manual Capacity Input */}
              <View style={styles.fieldContainerSmall}>
                <View style={styles.labelWithIcon}>
                  <Text style={[styles.label, { color: theme.colors.text }]}>
                    {isArabic ? 'سعة الخزان (لتر)' : 'Tank Capacity (L)'}
                  </Text>
                  {autoCalculateVolume && (
                    <View
                      style={[
                        styles.autoCalculateBadge,
                        { backgroundColor: theme.colors.success + '20' },
                      ]}
                    >
                      <Ionicons name="flash" size={12} color={theme.colors.success} />
                      <Text style={[styles.autoCalculateText, { color: theme.colors.success }]}>
                        {isArabic ? 'تلقائي' : 'Auto'}
                      </Text>
                    </View>
                  )}
                </View>
                <TextInput
                  style={[
                    styles.input,
                    {
                      backgroundColor: autoCalculateVolume
                        ? theme.colors.border + '20'
                        : theme.colors.background,
                      color: theme.colors.text,
                      borderColor: theme.colors.border,
                    },
                  ]}
                  value={tankCapacityLiters}
                  onChangeText={setTankCapacityLiters}
                  placeholder={isArabic ? 'مثال: 2000' : 'e.g., 2000'}
                  placeholderTextColor={theme.colors.textSecondary}
                  keyboardType="numeric"
                  editable={!autoCalculateVolume}
                />
              </View>

              <View style={styles.fieldContainerSmall}>
                <Text style={[styles.label, { color: theme.colors.text }]}>
                  {isArabic ? 'وحدة السعة' : 'Capacity Unit'}
                </Text>
                <View style={styles.unitToggleRow}>
                  <TouchableOpacity
                    style={[
                      styles.unitToggle,
                      { borderColor: theme.colors.border },
                      tankUnit === 'L' && { backgroundColor: theme.colors.primary },
                    ]}
                    onPress={() => setTankUnit('L')}
                  >
                    <Text
                      style={[
                        styles.unitToggleText,
                        { color: tankUnit === 'L' ? '#fff' : theme.colors.text },
                      ]}
                    >
                      L
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.unitToggle,
                      { borderColor: theme.colors.border },
                      tankUnit === 'm3' && { backgroundColor: theme.colors.primary },
                    ]}
                    onPress={() => setTankUnit('m3')}
                  >
                    <Text
                      style={[
                        styles.unitToggleText,
                        { color: tankUnit === 'm3' ? '#fff' : theme.colors.text },
                      ]}
                    >
                      m³
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.switchRow}>
                <View style={styles.switchLabelInline}>
                  <Ionicons name="warning-outline" size={20} color={theme.colors.warning} />
                  <Text style={[styles.label, { marginBottom: 0, color: theme.colors.text }]}>
                    {isArabic ? 'تنبيهات مستوى الخزان' : 'Tank Level Alerts'}
                  </Text>
                </View>
                <TouchableOpacity onPress={() => setTankAlertsEnabled(!tankAlertsEnabled)}>
                  <View
                    style={[
                      styles.switch,
                      tankAlertsEnabled && { backgroundColor: theme.colors.primary },
                    ]}
                  >
                    <View
                      style={[styles.switchThumb, tankAlertsEnabled && styles.switchThumbActive]}
                    />
                  </View>
                </TouchableOpacity>
              </View>

              {tankAlertsEnabled && (
                <View style={styles.row}>
                  <View style={[styles.fieldContainerSmall, styles.rowItem]}>
                    <Text style={[styles.label, { color: theme.colors.text }]}>
                      {isArabic ? 'حد منخفض (سم)' : 'Low Limit (cm)'}
                    </Text>
                    <TextInput
                      style={[
                        styles.input,
                        {
                          backgroundColor: theme.colors.background,
                          color: theme.colors.text,
                          borderColor: theme.colors.border,
                          textAlign: isRTL ? 'right' : 'left',
                        },
                      ]}
                      value={tankAlertLowCm}
                      onChangeText={setTankAlertLowCm}
                      placeholder={isArabic ? 'مثال: 20' : 'e.g., 20'}
                      placeholderTextColor={theme.colors.textSecondary}
                      keyboardType="numeric"
                    />
                  </View>

                  <View style={[styles.fieldContainerSmall, styles.rowItem]}>
                    <Text style={[styles.label, { color: theme.colors.text }]}>
                      {isArabic ? 'حد مرتفع (سم)' : 'High Limit (cm)'}
                    </Text>
                    <TextInput
                      style={[
                        styles.input,
                        {
                          backgroundColor: theme.colors.background,
                          color: theme.colors.text,
                          borderColor: theme.colors.border,
                          textAlign: isRTL ? 'right' : 'left',
                        },
                      ]}
                      value={tankAlertHighCm}
                      onChangeText={setTankAlertHighCm}
                      placeholder={isArabic ? 'مثال: 140' : 'e.g., 140'}
                      placeholderTextColor={theme.colors.textSecondary}
                      keyboardType="numeric"
                    />
                  </View>
                </View>
              )}

              <Text style={[styles.hint, { color: theme.colors.textSecondary }]}>
                {isArabic
                  ? 'يمكن ترك القيم فارغة وسيستخدم النظام افتراضيات العرض.'
                  : 'Leave empty if unknown; the system will use display defaults.'}
              </Text>
            </View>
          )}

          {/* ✅ P1-06: Site */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {isArabic ? 'الموقع' : 'Site'} *
            </Text>
            <TouchableOpacity
              style={[
                styles.input,
                styles.pickerButton,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
              onPress={() => setShowSitePicker(true)}
              accessible={true}
              accessibilityLabel={
                selectedSite
                  ? `${isArabic ? 'الموقع (مطلوب)' : 'Site (required)'}: ${selectedSite.name}`
                  : isArabic
                  ? 'الموقع (مطلوب): لم يتم الاختيار'
                  : 'Site (required): Not selected'
              }
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لاختيار الموقع من القائمة'
                  : 'Double tap to select site from the list'
              }
              accessibilityRole="button"
            >
              <Text
                style={[
                  styles.pickerText,
                  { color: selectedSite ? theme.colors.text : theme.colors.textSecondary },
                ]}
              >
                {selectedSite?.name || (isArabic ? 'اختر الموقع' : 'Select Site')}
              </Text>
              <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* ✅ P1-06: Zone */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {isArabic ? 'المنطقة' : 'Zone'} *
            </Text>
            <TouchableOpacity
              style={[
                styles.input,
                styles.pickerButton,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
              onPress={() => setShowZonePicker(true)}
              disabled={!selectedSite}
              accessible={true}
              accessibilityLabel={
                selectedZone
                  ? `${isArabic ? 'المنطقة (مطلوب)' : 'Zone (required)'}: ${selectedZone.name}`
                  : isArabic
                  ? 'المنطقة (مطلوب): لم يتم الاختيار'
                  : 'Zone (required): Not selected'
              }
              accessibilityHint={
                !selectedSite
                  ? isArabic
                    ? 'يجب اختيار الموقع أولاً'
                    : 'Select site first'
                  : isArabic
                  ? 'انقر نقرًا مزدوجًا لاختيار المنطقة من القائمة'
                  : 'Double tap to select zone from the list'
              }
              accessibilityRole="button"
              accessibilityState={{ disabled: !selectedSite }}
            >
              <Text
                style={[
                  styles.pickerText,
                  { color: selectedZone ? theme.colors.text : theme.colors.textSecondary },
                ]}
              >
                {selectedZone?.name || (isArabic ? 'اختر المنطقة' : 'Select Zone')}
              </Text>
              <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* ✅ P1-06: Alarm Rules Template */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {isArabic ? 'قالب قواعد التنبيهات' : 'Alarm Rules Template'}
            </Text>
            <TouchableOpacity
              style={[
                styles.input,
                styles.pickerButton,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
              onPress={() => setShowTemplatePicker(true)}
              accessible={true}
              accessibilityLabel={
                selectedTemplate
                  ? `${isArabic ? 'قالب قواعد التنبيهات' : 'Alarm rules template'}: ${
                      isArabic ? selectedTemplate.nameAr : selectedTemplate.name
                    }`
                  : isArabic
                  ? 'قالب قواعد التنبيهات: لا يوجد'
                  : 'Alarm rules template: None'
              }
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لاختيار قالب قواعد تنبيهات مسبق الإعداد (اختياري)'
                  : 'Double tap to select pre-configured alarm rules template (optional)'
              }
              accessibilityRole="button"
            >
              <View style={styles.pickerContent}>
                <Ionicons name="notifications-outline" size={20} color={theme.colors.warning} />
                <Text
                  style={[
                    styles.pickerText,
                    { color: selectedTemplate ? theme.colors.text : theme.colors.textSecondary },
                  ]}
                >
                  {selectedTemplate
                    ? isArabic
                      ? selectedTemplate.nameAr
                      : selectedTemplate.name
                    : isArabic
                    ? 'لا يوجد (يمكن الإضافة لاحقاً)'
                    : 'None (can add later)'}
                </Text>
              </View>
              <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
            </TouchableOpacity>
            {selectedTemplate && (
              <Text style={[styles.hint, { color: theme.colors.textSecondary }]}>
                {isArabic ? selectedTemplate.descriptionAr : selectedTemplate.description}
              </Text>
            )}
          </View>

          {/* ✅ P1-06: Has Battery */}
          <View
            style={[
              styles.switchContainer,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
          >
            <View style={styles.switchLabel}>
              <Ionicons name="battery-half" size={24} color={theme.colors.primary} />
              <View style={styles.switchTextContainer}>
                <Text style={[styles.label, { marginBottom: 0, color: theme.colors.text }]}>
                  {isArabic ? 'يحتوي على بطارية' : 'Has Battery'}
                </Text>
                <Text style={[styles.hint, { marginTop: 2, color: theme.colors.textSecondary }]}>
                  {isArabic
                    ? 'الجهاز يعمل بالبطارية ويرسل مستوى الشحن'
                    : 'Device is battery powered and reports battery level'}
                </Text>
              </View>
            </View>
            <TouchableOpacity
              onPress={() => setHasBattery(!hasBattery)}
              accessible={true}
              accessibilityLabel={isArabic ? 'يحتوي على بطارية' : 'Has battery'}
              accessibilityHint={
                hasBattery
                  ? isArabic
                    ? 'مفعّل. انقر نقرًا مزدوجًا إذا كان الجهاز لا يعمل بالبطارية'
                    : 'On. Tap if device is not battery powered'
                  : isArabic
                  ? 'غير مفعّل. انقر نقرًا مزدوجًا إذا كان الجهاز يعمل بالبطارية'
                  : 'Off. Tap if device is battery powered'
              }
              accessibilityRole="switch"
              accessibilityState={{ checked: hasBattery }}
            >
              <View
                style={[styles.switch, hasBattery && { backgroundColor: theme.colors.primary }]}
              >
                <View style={[styles.switchThumb, hasBattery && styles.switchThumbActive]} />
              </View>
            </TouchableOpacity>
          </View>

          {/* Info Box */}
          <View
            style={[
              styles.infoBox,
              { backgroundColor: theme.colors.info + '20', borderColor: theme.colors.info },
            ]}
          >
            <Ionicons name="information-circle" size={24} color={theme.colors.info} />
            <Text style={[styles.infoText, { color: theme.colors.text }]}>
              {isArabic
                ? 'سيتم توليد بيانات MQTT تلقائياً. احفظ اسم المستخدم وكلمة المرور لإعداد الجهاز.'
                : 'MQTT credentials will be generated automatically. Save the username and password to configure your device.'}
            </Text>
          </View>
        </View>
      </ScrollView>

      {/* ✅ P1-06: Save Button */}
      <View
        style={[
          styles.footer,
          { backgroundColor: theme.colors.card, borderTopColor: theme.colors.border },
        ]}
      >
        <TouchableOpacity
          style={[
            styles.saveButton,
            {
              backgroundColor: saving ? '#666' : theme.colors.primary,
              opacity: saving ? 0.7 : 1,
            },
          ]}
          onPress={handleSave}
          onPressIn={() => {
            // حماية فورية عند أول لمسة
            if (!isSavingRef.current && !saving) {
              logger.log('🔒 Button pressed, locking immediately');
            }
          }}
          disabled={saving}
          activeOpacity={saving ? 1 : 0.8}
          accessible={true}
          accessibilityLabel={
            saving
              ? isArabic
                ? 'جاري الحفظ...'
                : 'Saving...'
              : isArabic
              ? 'إضافة الجهاز'
              : 'Add device'
          }
          accessibilityHint={
            saving
              ? isArabic
                ? 'يرجى الانتظار أثناء حفظ الجهاز'
                : 'Please wait while device is being saved'
              : isArabic
              ? 'انقر نقرًا مزدوجًا لإنشاء الجهاز وتوليد بيانات MQTT'
              : 'Double tap to create device and generate MQTT credentials'
          }
          accessibilityRole="button"
          accessibilityState={{ disabled: saving, busy: saving }}
        >
          {saving ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <ActivityIndicator size="small" color="#ffffff" />
              <Text style={styles.saveButtonText}>{isArabic ? 'جاري الحفظ...' : 'Saving...'}</Text>
            </View>
          ) : (
            <>
              <Ionicons name="add-circle" size={24} color="#ffffff" />
              <Text style={styles.saveButtonText}>{isArabic ? 'إضافة الجهاز' : 'Add Device'}</Text>
            </>
          )}
        </TouchableOpacity>
      </View>

      {/* Device Type Picker Modal */}
      <Modal
        visible={showDeviceTypePicker}
        transparent
        animationType="slide"
        onRequestClose={() => setShowDeviceTypePicker(false)}
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={() => setShowDeviceTypePicker(false)}
          />
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={[styles.modalHeader, { borderBottomColor: theme.colors.border }]}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {isArabic ? 'اختر نوع الجهاز' : 'Select Device Type'}
              </Text>
              <TouchableOpacity
                onPress={() => setShowDeviceTypePicker(false)}
                accessible={true}
                accessibilityLabel={isArabic ? 'إغلاق' : 'Close'}
                accessibilityHint={
                  isArabic
                    ? 'انقر نقرًا مزدوجًا لإغلاق قائمة أنواع الأجهزة'
                    : 'Double tap to close device type list'
                }
                accessibilityRole="button"
              >
                <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <FlatList
              data={getAllDeviceTypes()}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[
                    styles.pickerItem,
                    { borderBottomColor: theme.colors.border },
                    deviceType === item && { backgroundColor: theme.colors.primary + '10' },
                  ]}
                  onPress={() => {
                    setDeviceType(item);
                    setShowDeviceTypePicker(false);
                  }}
                >
                  <Ionicons
                    name={getDeviceTypeIcon(item) as any}
                    size={24}
                    color={deviceType === item ? theme.colors.primary : theme.colors.textSecondary}
                  />
                  <Text
                    style={[
                      styles.pickerItemText,
                      { color: deviceType === item ? theme.colors.primary : theme.colors.text },
                    ]}
                  >
                    {getDeviceTypeLabel(item, isArabic ? 'ar' : 'en')}
                  </Text>
                  {deviceType === item && (
                    <Ionicons name="checkmark-circle" size={20} color={theme.colors.primary} />
                  )}
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      {/* Site Picker Modal */}
      <Modal
        visible={showSitePicker}
        transparent
        animationType="slide"
        onRequestClose={() => setShowSitePicker(false)}
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={() => setShowSitePicker(false)}
          />
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={[styles.modalHeader, { borderBottomColor: theme.colors.border }]}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {isArabic ? 'اختر الموقع' : 'Select Site'}
              </Text>
              <TouchableOpacity onPress={() => setShowSitePicker(false)}>
                <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <FlatList
              data={sites}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[
                    styles.pickerItem,
                    { borderBottomColor: theme.colors.border },
                    selectedSite?.id === item.id && {
                      backgroundColor: theme.colors.primary + '10',
                    },
                  ]}
                  onPress={() => {
                    setSelectedSite(item);
                    setSelectedZone(null);
                    setShowSitePicker(false);
                  }}
                >
                  <Ionicons
                    name="business"
                    size={24}
                    color={
                      selectedSite?.id === item.id
                        ? theme.colors.primary
                        : theme.colors.textSecondary
                    }
                  />
                  <Text
                    style={[
                      styles.pickerItemText,
                      {
                        color:
                          selectedSite?.id === item.id ? theme.colors.primary : theme.colors.text,
                      },
                    ]}
                  >
                    {item.name}
                  </Text>
                  {selectedSite?.id === item.id && (
                    <Ionicons name="checkmark-circle" size={20} color={theme.colors.primary} />
                  )}
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      {/* Zone Picker Modal */}

      {/* Zone Picker Modal */}
      <Modal
        visible={showZonePicker}
        transparent
        animationType="slide"
        onRequestClose={() => setShowZonePicker(false)}
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={() => setShowZonePicker(false)}
          />
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={[styles.modalHeader, { borderBottomColor: theme.colors.border }]}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {isArabic ? 'اختر المنطقة' : 'Select Zone'}
              </Text>
              <TouchableOpacity onPress={() => setShowZonePicker(false)}>
                <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <FlatList
              data={filteredZones}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[
                    styles.pickerItem,
                    { borderBottomColor: theme.colors.border },
                    selectedZone?.id === item.id && {
                      backgroundColor: theme.colors.primary + '10',
                    },
                  ]}
                  onPress={() => {
                    setSelectedZone(item);
                    setShowZonePicker(false);
                  }}
                >
                  <Ionicons
                    name="location"
                    size={24}
                    color={
                      selectedZone?.id === item.id
                        ? theme.colors.primary
                        : theme.colors.textSecondary
                    }
                  />
                  <Text
                    style={[
                      styles.pickerItemText,
                      {
                        color:
                          selectedZone?.id === item.id ? theme.colors.primary : theme.colors.text,
                      },
                    ]}
                  >
                    {item.name}
                  </Text>
                  {selectedZone?.id === item.id && (
                    <Ionicons name="checkmark-circle" size={20} color={theme.colors.primary} />
                  )}
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      {/* Alarm Template Picker Modal */}
      <Modal
        visible={showTemplatePicker}
        transparent
        animationType="slide"
        onRequestClose={() => setShowTemplatePicker(false)}
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={() => setShowTemplatePicker(false)}
          />
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={[styles.modalHeader, { borderBottomColor: theme.colors.border }]}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {isArabic ? 'اختر قالب قواعد التنبيهات' : 'Select Alarm Rules Template'}
              </Text>
              <TouchableOpacity onPress={() => setShowTemplatePicker(false)}>
                <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>

            {/* None Option */}
            <TouchableOpacity
              style={[
                styles.pickerItem,
                { borderBottomColor: theme.colors.border },
                !selectedTemplate && { backgroundColor: theme.colors.primary + '10' },
              ]}
              onPress={() => {
                setSelectedTemplate(null);
                setShowTemplatePicker(false);
              }}
            >
              <Ionicons
                name="close-circle"
                size={24}
                color={!selectedTemplate ? theme.colors.primary : theme.colors.textSecondary}
              />
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text
                  style={[
                    styles.pickerItemText,
                    { color: !selectedTemplate ? theme.colors.primary : theme.colors.text },
                  ]}
                >
                  {isArabic ? 'لا يوجد' : 'None'}
                </Text>
                <Text style={[styles.templateDescription, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'يمكن إضافة قواعد التنبيهات لاحقاً' : 'Can add alarm rules later'}
                </Text>
              </View>
              {!selectedTemplate && (
                <Ionicons name="checkmark-circle" size={20} color={theme.colors.primary} />
              )}
            </TouchableOpacity>

            <FlatList
              data={alarmTemplates}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[
                    styles.pickerItem,
                    { borderBottomColor: theme.colors.border },
                    selectedTemplate?.id === item.id && {
                      backgroundColor: theme.colors.primary + '10',
                    },
                  ]}
                  onPress={() => {
                    setSelectedTemplate(item);
                    setShowTemplatePicker(false);
                  }}
                >
                  <Ionicons
                    name="notifications"
                    size={24}
                    color={
                      selectedTemplate?.id === item.id
                        ? theme.colors.warning
                        : theme.colors.textSecondary
                    }
                  />
                  <View style={{ flex: 1, marginLeft: 12 }}>
                    <Text
                      style={[
                        styles.pickerItemText,
                        {
                          color:
                            selectedTemplate?.id === item.id
                              ? theme.colors.primary
                              : theme.colors.text,
                        },
                      ]}
                    >
                      {isArabic ? item.nameAr : item.name}
                    </Text>
                    <Text
                      style={[styles.templateDescription, { color: theme.colors.textSecondary }]}
                    >
                      {isArabic ? item.descriptionAr : item.description}
                    </Text>
                    <Text style={[styles.templateRulesCount, { color: theme.colors.info }]}>
                      {item.rules.length} {isArabic ? 'قاعدة' : 'rules'}
                    </Text>
                  </View>
                  {selectedTemplate?.id === item.id && (
                    <Ionicons name="checkmark-circle" size={20} color={theme.colors.primary} />
                  )}
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    padding: 16,
  },
  fieldContainer: {
    marginBottom: 20,
  },
  fieldContainerSmall: {
    marginBottom: 12,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  rowItem: {
    flex: 1,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    fontSize: 16,
  },
  hint: {
    fontSize: 12,
    marginTop: 4,
  },
  tankConfigCard: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  tankConfigHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  tankConfigTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  unitToggleRow: {
    flexDirection: 'row',
    gap: 12,
  },
  unitToggle: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  unitToggleText: {
    fontSize: 14,
    fontWeight: '600',
  },
  comingSoonBadge: {
    fontSize: 10,
    marginLeft: 4,
  },
  calculatedValueCard: {
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12,
  },
  calculatedValueHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  calculatedLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  calculatedValueRow: {
    marginLeft: 28,
  },
  calculatedValue: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 4,
  },
  calculatedValueSecondary: {
    fontSize: 13,
  },
  labelWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  autoCalculateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  autoCalculateText: {
    fontSize: 11,
    fontWeight: '600',
  },
  pickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pickerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  pickerText: {
    fontSize: 16,
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 20,
  },
  switchLabel: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
    gap: 12,
  },
  switchTextContainer: {
    flex: 1,
  },
  switch: {
    width: 50,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#ccc',
    padding: 3,
    justifyContent: 'center',
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  switchLabelInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  switchThumb: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#fff',
  },
  switchThumbActive: {
    alignSelf: 'flex-end',
  },
  infoBox: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
    alignItems: 'flex-start',
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  orgInfoCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
    marginBottom: 16,
  },
  orgInfoText: {
    flex: 1,
  },
  orgInfoLabel: {
    fontSize: 12,
    marginBottom: 2,
  },
  orgInfoValue: {
    fontSize: 15,
    fontWeight: '600',
  },
  roleInfoCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
    marginBottom: 16,
  },
  roleInfoText: {
    flex: 1,
  },
  roleInfoLabel: {
    fontSize: 12,
    marginBottom: 2,
  },
  roleInfoValue: {
    fontSize: 15,
    fontWeight: '600',
  },
  userInfoCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
    marginBottom: 16,
  },
  userInfoText: {
    flex: 1,
  },
  userInfoLabel: {
    fontSize: 12,
    marginBottom: 2,
  },
  userInfoValue: {
    fontSize: 15,
    fontWeight: '600',
  },
  qrButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 10,
    marginBottom: 24,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
  },
  qrButtonText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '600',
  },
  footer: {
    padding: 16,
    borderTopWidth: 1,
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  saveButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  pickerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    gap: 12,
    borderBottomWidth: 1,
  },
  pickerItemText: {
    flex: 1,
    fontSize: 16,
  },
  templateDescription: {
    fontSize: 12,
    marginTop: 4,
    lineHeight: 16,
  },
  templateRulesCount: {
    fontSize: 11,
    fontWeight: '600',
    marginTop: 4,
  },
});
